/**
 * « Appeler » avec le choix normal / masqué (propriétaire, 12/10/2026) : un
 * technicien qui appelle depuis son téléphone personnel peut cacher son
 * numéro. L'appel masqué compose `#31#` devant le numéro — le code des
 * opérateurs français pour masquer son numéro sur un seul appel.
 *
 * Android ouvre le composeur avec le code. iOS interdit à une application de
 * composer un numéro contenant `#` : on y propose de copier le numéro préfixé
 * et on rappelle le réglage du téléphone qui masque tous les appels.
 */
import { useState, type ReactNode } from 'react';
import { Check, Copy, EyeOff, Phone } from 'lucide-react';
import { Modal } from './ui.js';
import { buttonClass, type ButtonVariant } from './ui/action.js';

const MASK_PREFIX = '#31#';

/** Chiffres et « + » seulement : ce que comprend le composeur. */
export function dialable(phone: string): string {
  return phone.replace(/[^\d+]/g, '');
}

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  // iPadOS se présente comme un Mac : on le reconnaît à l'écran tactile.
  return /iPhone|iPad|iPod/.test(navigator.userAgent)
    || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
}

/**
 * Le choix d'appel (normal ou masqué), piloté de l'extérieur : le bouton
 * « Appeler » l'ouvre, et une entrée du menu « … » peut l'ouvrir aussi.
 */
export function CallChoiceModal(props: {
  open: boolean;
  onClose: () => void;
  phone: string;
  who?: string | undefined;
}): ReactNode {
  const [copied, setCopied] = useState(false);
  const number = dialable(props.phone);
  if (!number) return null;
  const masked = `${MASK_PREFIX}${number}`;
  const ios = isIos();
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(masked);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <Modal open={props.open} title={props.who ? `Appeler ${props.who}` : 'Appeler'} onClose={props.onClose}>
      <div className="call-choice">
        <a className="call-choice__option" href={`tel:${number}`} onClick={props.onClose}>
          <Phone size={20} aria-hidden="true" />
          <span>
            <strong>Appel normal</strong>
            <small>{props.phone} · votre numéro s’affiche.</small>
          </span>
        </a>
        {ios ? (
          <div className="call-choice__option call-choice__option--static">
            <EyeOff size={20} aria-hidden="true" />
            <span>
              <strong>Appel masqué</strong>
              <small>
                Sur iPhone, Apple ne laisse pas une application composer le code de masquage.
                Copiez le numéro ci-dessous et collez-le dans Téléphone, ou masquez tous vos
                appels dans Réglages → Téléphone → Afficher mon numéro.
              </small>
              <button type="button" className="btn btn--secondary" onClick={() => void copy()}>
                {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
                {copied ? 'Copié' : `Copier ${masked}`}
              </button>
            </span>
          </div>
        ) : (
          <a className="call-choice__option" href={`tel:${encodeURIComponent(masked)}`} onClick={props.onClose}>
            <EyeOff size={20} aria-hidden="true" />
            <span>
              <strong>Appel masqué</strong>
              <small>Votre numéro est caché pour cet appel (code {MASK_PREFIX}, selon votre opérateur).</small>
            </span>
          </a>
        )}
      </div>
    </Modal>
  );
}

export function CallButton(props: {
  phone: string;
  /** Qui l'on appelle, pour le titre du choix (« Superette du Centre »). */
  who?: string | undefined;
  /** Apparence du système de boutons (secondaire par défaut). */
  variant?: ButtonVariant;
  large?: boolean;
  /** Pictogramme seul : le libellé passe par `ariaLabel`. */
  iconOnly?: boolean;
  ariaLabel?: string;
  /** Ancienne façon de choisir l'apparence, gardée le temps de la migration des écrans. */
  className?: string;
  children: ReactNode;
}): ReactNode {
  const [open, setOpen] = useState(false);
  if (!dialable(props.phone)) return null;
  return (
    <>
      <button
        type="button"
        className={props.className ?? buttonClass(props.variant ?? 'secondary', props.large, Boolean(props.iconOnly))}
        aria-label={props.ariaLabel}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        {props.children}
      </button>
      <CallChoiceModal open={open} onClose={() => setOpen(false)} phone={props.phone} who={props.who} />
    </>
  );
}
